import {
  BatchType,
  SaveBatchTypeRequest,
  PackagingType,
  SavePackagingTypeRequest,
  BatchTypePackage,
  UpdateBatchTypePackageRequest
} from '../types/batchPickingSetup';
import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/** Leser feilmeldingen fra API-et ({ error }) slik at valideringsteksten vises for brukeren. */
async function throwApiError(response: Response): Promise<never> {
  const text = await response.text();
  let message = text;
  try {
    const body = JSON.parse(text);
    if (body?.error) message = body.error;
  } catch {
    // ikke JSON — bruk teksten som den er
  }
  throw new Error(message || `HTTP error! status: ${response.status}`);
}

class BatchPickingSetupServiceClass {
  private getBaseUrl(): string {
    const userContext = AuthService.getUserDataFromToken();
    if (!userContext) {
      throw new Error('No user context available');
    }
    return `${API_BASE_URL}/${userContext.companyNo}/batch-picking-setup`;
  }

  // Samplukk-typer

  async getBatchTypes(): Promise<BatchType[]> {
    const response = await AuthService.authenticatedGet(`${this.getBaseUrl()}/types`);
    if (!response.ok) await throwApiError(response);
    const data = await response.json();
    return Array.isArray(data) ? data.map((d) => ({ ...d, id: d.pListBatchTypeNo })) : [];
  }

  async createBatchType(request: SaveBatchTypeRequest): Promise<BatchType> {
    const response = await AuthService.authenticatedPost(`${this.getBaseUrl()}/types`, request);
    if (!response.ok) await throwApiError(response);
    return await response.json();
  }

  async updateBatchType(batchTypeNo: number, request: SaveBatchTypeRequest): Promise<BatchType> {
    const response = await AuthService.authenticatedPut(`${this.getBaseUrl()}/types/${batchTypeNo}`, request);
    if (!response.ok) await throwApiError(response);
    return await response.json();
  }

  async deleteBatchType(batchTypeNo: number): Promise<void> {
    const response = await AuthService.authenticatedFetch(`${this.getBaseUrl()}/types/${batchTypeNo}`, { method: 'DELETE' });
    if (!response.ok) await throwApiError(response);
  }

  // Esker

  async getPackagingTypes(): Promise<PackagingType[]> {
    const response = await AuthService.authenticatedGet(`${this.getBaseUrl()}/packaging-types`);
    if (!response.ok) await throwApiError(response);
    const data = await response.json();
    return Array.isArray(data) ? data.map((d) => ({ ...d, id: d.packagingTypeNo })) : [];
  }

  async createPackagingType(request: SavePackagingTypeRequest): Promise<PackagingType> {
    const response = await AuthService.authenticatedPost(`${this.getBaseUrl()}/packaging-types`, request);
    if (!response.ok) await throwApiError(response);
    return await response.json();
  }

  async updatePackagingType(packagingTypeNo: number, request: SavePackagingTypeRequest): Promise<PackagingType> {
    const response = await AuthService.authenticatedPut(`${this.getBaseUrl()}/packaging-types/${packagingTypeNo}`, request);
    if (!response.ok) await throwApiError(response);
    return await response.json();
  }

  async deletePackagingType(packagingTypeNo: number): Promise<void> {
    const response = await AuthService.authenticatedFetch(`${this.getBaseUrl()}/packaging-types/${packagingTypeNo}`, { method: 'DELETE' });
    if (!response.ok) await throwApiError(response);
  }

  // Innhold

  async getBatchTypePackages(batchTypeNo: number): Promise<BatchTypePackage[]> {
    const response = await AuthService.authenticatedGet(`${this.getBaseUrl()}/types/${batchTypeNo}/packages`);
    if (!response.ok) await throwApiError(response);
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  async addBatchTypePackage(batchTypeNo: number, packagingTypeNo: number, numOfPackages: number | null): Promise<BatchTypePackage> {
    const response = await AuthService.authenticatedPost(`${this.getBaseUrl()}/types/${batchTypeNo}/packages`, { packagingTypeNo, numOfPackages });
    if (!response.ok) await throwApiError(response);
    return await response.json();
  }

  /** Lagrer alle feltene på koblingen. Samplukk-type og eske kan byttes. */
  async updateBatchTypePackage(batchTypeNo: number, packagingTypeNo: number, request: UpdateBatchTypePackageRequest): Promise<BatchTypePackage> {
    const response = await AuthService.authenticatedPut(`${this.getBaseUrl()}/types/${batchTypeNo}/packages/${packagingTypeNo}`, request);
    if (!response.ok) await throwApiError(response);
    return await response.json();
  }

  async removeBatchTypePackage(batchTypeNo: number, packagingTypeNo: number): Promise<void> {
    const response = await AuthService.authenticatedFetch(`${this.getBaseUrl()}/types/${batchTypeNo}/packages/${packagingTypeNo}`, { method: 'DELETE' });
    if (!response.ok) await throwApiError(response);
  }

  async reorderBatchTypePackages(batchTypeNo: number, packagingTypeNos: number[]): Promise<BatchTypePackage[]> {
    const response = await AuthService.authenticatedPut(`${this.getBaseUrl()}/types/${batchTypeNo}/packages/order`, { packagingTypeNos });
    if (!response.ok) await throwApiError(response);
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }
}

export const batchPickingSetupService = new BatchPickingSetupServiceClass();
